import React from "react";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import chroma from "chroma-js";

type RtpStats = Record<string, unknown> | null | undefined;

const IN_DETAIL_KEYS = [
  "rtp_audio_in_mos",
  "rtp_audio_in_packet_loss",
  "rtp_audio_in_jitter",
  "rtp_audio_in_skip_packet_count",
] as const;

const OUT_DETAIL_KEYS = [
  "rtp_audio_out_mos",
  "rtp_audio_out_packet_loss",
  "rtp_audio_out_jitter",
  "rtp_audio_out_skip_packet_count",
] as const;

const DETAIL_LABELS: Record<string, string> = {
  rtp_audio_in_mos: "MOS",
  rtp_audio_out_mos: "MOS",
  rtp_audio_in_packet_loss: "Packet Loss",
  rtp_audio_out_packet_loss: "Packet Loss",
  rtp_audio_in_jitter: "Jitter",
  rtp_audio_out_jitter: "Jitter",
  rtp_audio_in_skip_packet_count: "Skip Packet Count",
  rtp_audio_out_skip_packet_count: "Skip Packet Count",
};

const getMosColor = (mos: number | null) => {
  if (mos === null) {
    return { color: "rgba(0, 0, 0, 0.45)", background: "#f1f4fa" };
  }
  if (mos < 3) {
    return { color: "#d32f2f", background: chroma("#d32f2f").alpha(0.15).hex() };
  }
  if (mos <= 4) {
    return { color: "#f9a825", background: chroma("#f9a825").alpha(0.2).hex() };
  }
  return { color: "#2e7d32", background: chroma("#2e7d32").alpha(0.15).hex() };
};

const toMosNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const formatDetailValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  return String(value);
};

const DetailTooltip = ({
  label,
  keys,
  rtpStats,
}: {
  label: string;
  keys: readonly string[];
  rtpStats: Record<string, unknown>;
}) => {
  const hasAny = keys.some(
    (key) => rtpStats[key] !== null && rtpStats[key] !== undefined
  );

  if (!hasAny) {
    return (
      <Typography sx={{ fontSize: 12, color: "#fff" }}>
        No {label} MOS stats
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 0.5, minWidth: 170 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: "#fff" }}>
        {label} MOS
      </Typography>
      {keys.map((key) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
            {DETAIL_LABELS[key] || key}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#fff", fontWeight: 500 }}>
            {formatDetailValue(rtpStats[key])}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const MosChip = ({
  label,
  mos,
  detailKeys,
  rtpStats,
}: {
  label: "In" | "Out";
  mos: number | null;
  detailKeys: readonly string[];
  rtpStats: Record<string, unknown>;
}) => {
  const { color, background } = getMosColor(mos);
  const display = mos === null ? "--" : mos.toFixed(2);

  return (
    <Tooltip
      title={
        <DetailTooltip label={label} keys={detailKeys} rtpStats={rtpStats} />
      }
      placement="bottom"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#111111",
            maxWidth: 320,
            "& .MuiTooltip-arrow": { color: "#111111" },
          },
        },
      }}
    >
      <Box
        className="flex-xy-center"
        sx={{
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: "8px",
          backgroundColor: background,
          cursor: "default",
          minWidth: 58,
        }}
      >
        <Typography
          sx={{ fontSize: 11, fontWeight: 500, color: "rgba(0,0,0,0.55)" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color }}>
          {display}
        </Typography>
      </Box>
    </Tooltip>
  );
};

type RtpStatsCellProps = {
  rtpStats?: RtpStats;
};

const RtpStatsCell: React.FC<RtpStatsCellProps> = ({ rtpStats }) => {
  if (!rtpStats || typeof rtpStats !== "object") {
    return (
      <Typography className="font-12-mediumgrey" sx={{ textAlign: "center" }}>
        --
      </Typography>
    );
  }

  const inMos = toMosNumber(rtpStats.rtp_audio_in_mos);
  const outMos = toMosNumber(rtpStats.rtp_audio_out_mos);

  if (inMos === null && outMos === null) {
    return (
      <Typography className="font-12-mediumgrey" sx={{ textAlign: "center" }}>
        --
      </Typography>
    );
  }

  return (
    <Box className="flex-xy-center" gap={1} mr={2}>
      <MosChip
        label="In"
        mos={inMos}
        detailKeys={IN_DETAIL_KEYS}
        rtpStats={rtpStats}
      />
      <Divider orientation="vertical" flexItem />
      <MosChip
        label="Out"
        mos={outMos}
        detailKeys={OUT_DETAIL_KEYS}
        rtpStats={rtpStats}
      />
    </Box>
  );
};

export default RtpStatsCell;
